import React, { useMemo, useState, useEffect } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  DocumentIcon,
  DocumentTextIcon,
  PhotoIcon,
  PencilIcon,
  ClockIcon,
  TrashIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { TanTable } from '../../../../components/TanTable';
import DocFileIconButton from '../../../../components/docs/DocFileIconButton';
import PrintButton from '../../../../components/printing/PrintButton';
import PermissionsContainer from '../../../../components/PermissionsContainer';
import type { ProDocumentRow } from '../../../../types/proDocuments';
import {
  FileType,
  FILE_TYPE_LABEL,
  resolveFileType,
} from '../../../../types/fileType';

export interface DocsTableProps {
  rows: ProDocumentRow[];
  onEdit: (row: ProDocumentRow) => void;
  onHistory: (row: ProDocumentRow) => void;
  onDelete: (row: ProDocumentRow) => void;
  onPreview: (row: ProDocumentRow) => void;
  onToggleInvoice: (row: ProDocumentRow, value: 'Y' | 'N') => void;
  /**
   * Set the Invoice flag on every eligible row at once (V1's header checkbox,
   * `togglebyqueryall(this,'documentlist_invoice')`, lib_editrecord.pl:7915).
   *
   * Optional, and DocsTab supplies it ONLY when the flags stage into the order save.
   * Without staging each row would be its own PUT, so one click would fire a whole
   * document list's worth of writes — the behaviour this change exists to remove.
   */
  onSetAllInvoice?: (rows: ProDocumentRow[], value: 'Y' | 'N') => void;
  onSelectionChange?: (selected: ProDocumentRow[]) => void;
  /** When set (change-all mode), each row shows a "Copy to all" action. */
  onCopyToAll?: (row: ProDocumentRow) => void;
  /** Show the per-row "Print" action on stored PDF documents (print-send gated).
   *  Default true. Staged (unsaved) docs pass false: there is nothing on the server
   *  to spool yet. */
  enablePrint?: boolean;
  loading?: boolean;
  emptyMessage?: string;
}

/** System-generated invoice PDF (cmsinvoice_map): read-only — preview only,
 *  no edit/delete/history/invoice-flag (V1 parity). */
function isInvoiceDoc(row: ProDocumentRow): boolean {
  return row.source === 'invoice';
}

/** Row identity: cmsinvoice_map recnos share the integer space with cms_map,
 *  so keys carry the source to stay unique across both. */
function rowKey(row: ProDocumentRow): string {
  return `${isInvoiceDoc(row) ? 'invoice' : 'cms'}-${row.map_recno}`;
}

/** MM/DD/YYYY HH:MM in the viewer's local zone; the raw value on a bad parse.
 *  Exported so the multi-PRO Documents view formats upload dates identically. */
export function formatUploadDate(value: string | null | undefined): string {
  if (!value) return '';
  let dateObj: Date;
  if (value.includes('T')) {
    dateObj = new Date(value);
  } else if (value.includes(' ')) {
    const [datePart, timePart] = value.split(' ');
    dateObj = new Date(`${datePart}T${timePart}`);
  } else {
    dateObj = new Date(`${value}T00:00:00`);
  }
  if (Number.isNaN(dateObj.getTime())) return value;
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const year = String(dateObj.getFullYear());
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  return `${month}/${day}/${year} ${hours}:${minutes}`;
}

function FileIconButton({
  row,
  onPreview,
}: {
  row: ProDocumentRow;
  onPreview: (row: ProDocumentRow) => void;
}) {
  return (
    <DocFileIconButton
      filetype={row.filetype}
      onPreview={() => onPreview(row)}
      testId="docs-table-file-icon"
    />
  );
}

/** Inline filetype tag (e.g. "PDF") shown next to the doctype text. */
function FileTypeTag({ filetype }: { filetype: string | null | undefined }) {
  const t = resolveFileType(filetype);
  return (
    <span
      className="text-[0.625rem] font-semibold tracking-wider px-1 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 uppercase"
      title={(filetype || '').toLowerCase()}
    >
      {FILE_TYPE_LABEL[t]}
    </span>
  );
}

/**
 * The per-document Invoice (Y/N) flag.
 *
 * Still a native <select> — the clerks asked for the dropdown to behave, not to become
 * a different control — but with the two things that made it hard to drive:
 *
 * FULL-CELL TARGET. It used to be a shrink-wrapped control roughly as wide as the letter
 * in it, inside an 80px column, so most of the cell was dead space that did nothing when
 * clicked. `w-full` plus a 28px height makes the whole cell the target.
 *
 * TYPING IS LEFT TO THE BROWSER. A native select already type-aheads — pressing Y or N
 * with the menu open picks that option — and an onKeyDown of our own would fight it on
 * the platforms that render the menu in-page rather than as an OS popup. V1 uses the
 * same native control (smod_box with @noyes_data), so this matches it.
 */
function InvoiceSelect({
  row,
  onToggleInvoice,
}: {
  row: ProDocumentRow;
  onToggleInvoice: (row: ProDocumentRow, value: 'Y' | 'N') => void;
}) {
  return (
    <select
      value={row.forinvoice}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation();
        onToggleInvoice(row, e.target.value === 'Y' ? 'Y' : 'N');
      }}
      aria-label="Invoice flag"
      title="Include this document with the invoice"
      className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded bg-panel text-heading px-1 h-7 cursor-pointer focus:ring-2 focus:ring-blue-500"
    >
      <option value="Y">Y</option>
      <option value="N">N</option>
    </select>
  );
}

/**
 * V1's set-all checkbox, in the Invoice column header.
 *
 * V1 semantics exactly (cms.js::togglebyqueryall): a SET-ALL, not a per-row flip.
 * Ticking it writes Y to every row, clearing it writes N to every row; a row already
 * holding the target value is simply left alone.
 *
 * A mixed column reads as UNCHECKED rather than indeterminate — deliberately. The dash
 * glyph suggests a third state the control does not have, and the useful reading of the
 * box is "are they all on", which a mixed set answers with no. Clicking from mixed sets
 * everything to Y, clicking again sets everything to N.
 *
 * Rows V1 leaves blank here (system-generated invoice PDFs) are excluded from both the
 * state and the write.
 *
 * Rendered through the column's `meta.headerTrailing`, so TanTable places it after the
 * pin and sort icons at the far right of the header rather than beside the label.
 */
function InvoiceHeader({
  rows,
  onSetAll,
}: {
  rows: ProDocumentRow[];
  onSetAll: (rows: ProDocumentRow[], value: 'Y' | 'N') => void;
}) {
  const eligible = rows.filter((r) => !isInvoiceDoc(r));
  const allY =
    eligible.length > 0 && eligible.every((r) => r.forinvoice === 'Y');
  return (
    <span className="inline-flex items-center flex-shrink-0">
      <input
        type="checkbox"
        checked={allY}
        disabled={eligible.length === 0}
        onChange={(e) => onSetAll(eligible, e.target.checked ? 'Y' : 'N')}
        onClick={(e) => e.stopPropagation()}
        aria-label="Set Invoice on all documents"
        title={allY ? 'Clear Invoice on all documents' : 'Set Invoice on all documents'}
        className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
      />
    </span>
  );
}

/** Send a stored PDF to a CUPS laser queue. Same endpoint the preview lightbox uses;
 *  invoice-source rows carry `?source=invoice` so the API reads cmsinvoice_map. Only
 *  PDFs: CUPS converts PDF, and the API 409s anything else before downloading it. */
function RowPrintButton({ row }: { row: ProDocumentRow }) {
  if (row.deleted === 'Y' || resolveFileType(row.filetype) !== FileType.Pdf) return null;
  const endpoint = `/pros/${row.pronumber}/documents/${row.map_recno}/print${
    isInvoiceDoc(row) ? '?source=invoice' : ''
  }`;
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <PrintButton
        endpoint={endpoint}
        iconOnly
        label="Print"
        title={`Print ${row.documenttype || 'document'}`}
        className="p-1 rounded text-gray-500 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-700"
      />
    </span>
  );
}

/** Component permission gating Delete on a system-generated invoice PDF. Seeded by
 *  migration 525 and re-resolved server-side in `delete_pro_document` — the browser
 *  gate is advisory, the grant is what authorizes the write. Keep the id in step with
 *  `pro_documents.INVOICE_DOC_DELETE_COMPONENT`. */
export const INVOICE_DOC_DELETE_PERMISSION_ID = 'invoice-doc-delete';
export const INVOICE_DOC_DELETE_PERMISSION_NAME = 'Pro Docs — Delete Invoice Document';

/** Delete for an invoice PDF — the ONE write an invoice-source row offers, and only to
 *  a granted caller (defaultHidden, so no icon at all otherwise). The use case is
 *  cancel-and-reinvoice: the archived PDF stops describing what the customer owes, so
 *  the stale one comes off the pro. Everything else on these rows stays read-only. */
function RowInvoiceDeleteButton({
  row,
  onDelete,
}: {
  row: ProDocumentRow;
  onDelete: (row: ProDocumentRow) => void;
}) {
  if (row.deleted === 'Y') return null;
  return (
    <PermissionsContainer
      componentId={INVOICE_DOC_DELETE_PERMISSION_ID}
      componentName={INVOICE_DOC_DELETE_PERMISSION_NAME}
      defaultHidden
      showHandle={false}
      className="inline-flex"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(row);
        }}
        className="p-1 rounded text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
        aria-label="Delete invoice document"
        title="Delete this invoice document"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </PermissionsContainer>
  );
}

function RowActions({
  row,
  onEdit,
  onHistory,
  onDelete,
  onCopyToAll,
  enablePrint,
}: {
  row: ProDocumentRow;
  onEdit: (row: ProDocumentRow) => void;
  onHistory: (row: ProDocumentRow) => void;
  onDelete: (row: ProDocumentRow) => void;
  onCopyToAll?: (row: ProDocumentRow) => void;
  enablePrint: boolean;
}) {
  const isDeleted = row.deleted === 'Y';
  return (
    <div className="flex items-center gap-1">
      {enablePrint && <RowPrintButton row={row} />}
      {onCopyToAll && !isDeleted && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCopyToAll(row);
          }}
          className="p-1 rounded text-gray-500 hover:text-amber-600 hover:bg-gray-100 dark:hover:bg-gray-700"
          aria-label="Copy document to all"
          title="Copy to all"
        >
          <DocumentDuplicateIcon className="h-4 w-4" />
        </button>
      )}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(row);
        }}
        className="p-1 rounded text-gray-500 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-700"
        aria-label="Edit document"
      >
        <PencilIcon className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onHistory(row);
        }}
        className="p-1 rounded text-gray-500 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-700"
        aria-label="View document history"
      >
        <ClockIcon className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!isDeleted) onDelete(row);
        }}
        disabled={isDeleted}
        aria-disabled={isDeleted}
        className={`p-1 rounded ${
          isDeleted
            ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
            : 'text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
        aria-label="Delete document"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function DocsTable({
  rows,
  onEdit,
  onHistory,
  onDelete,
  onPreview,
  onToggleInvoice,
  onSetAllInvoice,
  onSelectionChange,
  onCopyToAll,
  enablePrint = true,
  loading = false,
  emptyMessage = 'No documents.',
}: DocsTableProps) {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  // Drop selection for rows that no longer exist when rows change.
  useEffect(() => {
    setSelectedKeys((prev) => {
      const validKeys = new Set(rows.map(rowKey));
      let changed = false;
      const next = new Set<string>();
      prev.forEach((k) => {
        if (validKeys.has(k)) next.add(k);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [rows]);

  // Notify parent when selection changes.
  useEffect(() => {
    if (!onSelectionChange) return;
    const selected = rows.filter((r) => selectedKeys.has(rowKey(r)));
    onSelectionChange(selected);
    // Re-run when selection or rows change; onSelectionChange is treated as stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKeys, rows]);

  const allSelected = rows.length > 0 && rows.every((r) => selectedKeys.has(rowKey(r)));
  const someSelected = rows.some((r) => selectedKeys.has(rowKey(r)));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(rows.map(rowKey)));
    }
  };

  const toggleRow = (row: ProDocumentRow) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey(row))) next.delete(rowKey(row));
      else next.add(rowKey(row));
      return next;
    });
  };

  const columns = useMemo<ColumnDef<ProDocumentRow>[]>(
    () => [
      {
        id: 'select',
        size: 40,
        enableSorting: false,
        enableColumnFilter: false,
        header: () => (
          <input
            type="checkbox"
            aria-label="Select all documents"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = !allSelected && someSelected;
            }}
            onChange={(e) => {
              e.stopPropagation();
              toggleAll();
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ),
        cell: ({ row }) => {
          const r = row.original;
          return (
            <input
              type="checkbox"
              aria-label={`Select document ${r.map_recno}`}
              checked={selectedKeys.has(rowKey(r))}
              onChange={(e) => {
                e.stopPropagation();
                toggleRow(r);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          );
        },
      },
      {
        id: 'documenttype',
        accessorKey: 'documenttype',
        header: 'Type',
        size: 220,
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5 min-w-0">
            <FileIconButton row={row.original} onPreview={onPreview} />
            <FileTypeTag filetype={row.original.filetype} />
            <span className="truncate">
              {/* V1 parity: deleted docs keep their row, prefixed "DELETED - ". */}
              {(row.original.deleted === 'Y' ? 'DELETED - ' : '') +
                (row.original.documenttype || '')}
            </span>
          </div>
        ),
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: 'Description',
        size: 240,
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ row }) => <span>{row.original.description || ''}</span>,
      },
      {
        id: 'driver',
        accessorKey: 'driver',
        header: 'Driver',
        size: 100,
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        cell: ({ row }) => <span>{row.original.driver || ''}</span>,
      },
      {
        id: 'uploaddatetime',
        accessorKey: 'uploaddatetime',
        header: 'Upload Date',
        size: 150,
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ row }) => <span>{formatUploadDate(row.original.uploaddatetime)}</span>,
      },
      {
        id: 'uploadedby',
        accessorKey: 'uploadedby',
        header: 'Uploaded By',
        size: 130,
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        cell: ({ row }) => <span>{row.original.uploadedby || ''}</span>,
      },
      {
        id: 'forinvoice',
        accessorKey: 'forinvoice',
        // A STRING header, with the set-all control in meta.headerTrailing — TanTable
        // renders that after the pin/sort icons, at the far right of the header. Keeping
        // the header a string also matters beyond layout: the column picker, the export
        // and autosize all read it only when it is a string and fall back to the column
        // id otherwise.
        header: 'Invoice',
        meta: onSetAllInvoice
          // Scoped to the rows this grid was handed — the Docs tab's filter set — which
          // matches V1, whose selector hits every rendered dropdown.
          ? { headerTrailing: <InvoiceHeader rows={rows} onSetAll={onSetAllInvoice} /> }
          : undefined,
        size: 80,
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        cell: ({ row }) =>
          // Invoice docs have no toggle — V1 leaves the column blank for them.
          isInvoiceDoc(row.original) ? null : (
            <InvoiceSelect row={row.original} onToggleInvoice={onToggleInvoice} />
          ),
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 110,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) =>
          // Invoice docs stay read-only except for Delete: preview via the file icon,
          // Print — the archived PDF is exactly what a clerk wants on paper — and, for a
          // caller granted `invoice-doc-delete`, remove a PDF a cancel-and-reinvoice has
          // made stale. Still no edit/history/invoice-flag (V1 parity).
          isInvoiceDoc(row.original) ? (
            <div className="flex items-center gap-1">
              {enablePrint && <RowPrintButton row={row.original} />}
              <RowInvoiceDeleteButton row={row.original} onDelete={onDelete} />
            </div>
          ) : (
            <RowActions
              row={row.original}
              onEdit={onEdit}
              onHistory={onHistory}
              onDelete={onDelete}
              onCopyToAll={onCopyToAll}
              enablePrint={enablePrint}
            />
          ),
      },
    ],
    [
      allSelected,
      someSelected,
      selectedKeys,
      onPreview,
      onToggleInvoice,
      onSetAllInvoice,
      rows,
      onEdit,
      onHistory,
      onDelete,
      onCopyToAll,
      enablePrint,
    ],
  );

  if (loading) {
    return (
      <div
        role="status"
        aria-label="Loading documents"
        className="flex items-center justify-center p-8 text-sm text-muted"
      >
        <span
          className="inline-block h-5 w-5 mr-2 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
          aria-hidden="true"
        />
        Loading documents...
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted">
        {emptyMessage}
      </div>
    );
  }

  return (
    <TanTable<ProDocumentRow>
      data={rows}
      columns={columns}
      settingsKey="pro-docs-table"
      enablePagination={false}
      enableColumnFilters={true}
      enableExcelExport={false}
      enableSearch={false}
      fillContainer
      getRowId={rowKey}
      // V1 renders deleted docs on a solid grey row (bgcolor #AAAAAA).
      getRowClassName={(row) =>
        row.deleted === 'Y' ? 'bg-gray-200 dark:bg-gray-700 text-muted' : ''
      }
    />
  );
}
